"use client";

import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { SearchPageData } from "@providers/api/search-page-api";
import { Button, Combobox, ComboboxOption, SearchInput } from "@saridana-core/react-ui-kit";
import { TFaq } from "@shared/types";
import { HtmlParser } from "@shared/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { searchCategories } from "../constants";
import { fetchArticles, fetchFaqs, fetchProducts, normalizeArticles, normalizeFaqs, normalizeProducts } from "../lib";
import { SearchCategory } from "../types";
import { FaqSearchDialog } from "./faq-search-dialog";
import { SearchCategoryIcon } from "./search-category-icon";
export function SearchContainer({
  pageData
}: {
  pageData: Omit<SearchPageData, "seo">;
}) {
  const searchParams = useSearchParams();
  const keywordParam = searchParams.get("keyword") || "";
  const router = useRouter();
  const pathname = usePathname();
  const createQueryString = useCallback((name: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(name, value);
    return params.toString();
  }, [searchParams]);
  const [tempKeyword, setTempKeyword] = useState(keywordParam);
  const [keyword, setKeyword] = useState(keywordParam);
  useEffect(() => {
    setKeyword(keywordParam);
    setTempKeyword(keywordParam);
  }, [keywordParam]);
  const handleSearch = (val: string) => {
    setTempKeyword(val);
  };
  const handleSubmitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setKeyword(tempKeyword);
    router.push(pathname + "?" + createQueryString("keyword", tempKeyword));
  };
  const [selectedCategory, setSelectedCategory] = useState<ComboboxOption | null>(null);
  const handleChangeCategory = (option: ComboboxOption) => {
    setSelectedCategory(option);
  };
  const handleClearCategory = () => {
    setSelectedCategory({
      label: "Semua Kategori",
      value: null
    });
  };
  const options: any = {
    initialPageParam: 0,
    getNextPageParam: (lastPage: any) => {
      const meta = lastPage.meta.pagination;
      const next = meta.start + meta.limit;
      return next < meta.total ? next : undefined;
    },
    enabled: true
  };
  const {
    data: articles,
    fetchNextPage: fetchArticlesNextPage,
    hasNextPage: isArticlesHasNextPage,
    isFetchingNextPage: isArticlesFetchingNextPage,
    isFetching: isArticlesFetching
  } = useInfiniteQuery({
    queryKey: ["search-container-articles", keyword],
    queryFn: ({
      pageParam
    }) => fetchArticles({
      pageParam,
      keyword
    }),
    ...options
  });
  const {
    data: products,
    fetchNextPage: fetchProductsNextPage,
    hasNextPage: isProductsHasNextPage,
    isFetchingNextPage: isProductsFetchingNextPage,
    isFetching: isProductsFetching
  } = useInfiniteQuery({
    queryKey: ["search-container-products", keyword],
    queryFn: ({
      pageParam: pageParam_0
    }) => fetchProducts({
      pageParam: pageParam_0,
      keyword
    }),
    ...options
  });
  const {
    data: faqs,
    fetchNextPage: fetchFaqsNextPage,
    hasNextPage: isFaqsHasNextPage,
    isFetchingNextPage: isFaqsFetchingNextPage,
    isFetching: isFaqsFetching
  } = useInfiniteQuery({
    queryKey: ["search-container-faqs", keyword],
    queryFn: ({
      pageParam: pageParam_1
    }) => fetchFaqs({
      pageParam: pageParam_1,
      keyword
    }),
    ...options
  });
  const combinedResults = useMemo(() => {
    const list: any[] = [];
    const pushPages = (queryData: any, normalizer: any) => {
      queryData?.pages?.forEach((p: any) => {
        if (p?.data) list.push(...normalizer(p.data));
      });
    };
    if (selectedCategory === null || selectedCategory?.value === null) {
      pushPages(articles, normalizeArticles);
      pushPages(products, normalizeProducts);
      pushPages(faqs, normalizeFaqs);
    } else {
      switch (selectedCategory?.value as SearchCategory) {
        case "article":
          pushPages(articles, normalizeArticles);
          break;
        case "product":
          pushPages(products, normalizeProducts);
          break;
        case "faq":
          pushPages(faqs, normalizeFaqs);
          break;
      }
    }
    return list.sort((a, b) => a.title.localeCompare(b.title));
  }, [selectedCategory, articles, products, faqs]);
  const isFetching = isArticlesFetching || isArticlesFetchingNextPage || isFaqsFetching || isFaqsFetchingNextPage || isProductsFetching || isProductsFetchingNextPage;
  const loadMoreTargets = useMemo(() => {
    return [{
      key: "articles",
      hasNextPage: isArticlesHasNextPage,
      fetch: fetchArticlesNextPage
    }, {
      key: "products",
      hasNextPage: isProductsHasNextPage,
      fetch: fetchProductsNextPage
    }, {
      key: "faqs",
      hasNextPage: isFaqsHasNextPage,
      fetch: fetchFaqsNextPage
    }].filter(item => item.hasNextPage);
  }, [isArticlesHasNextPage, isProductsHasNextPage, isFaqsHasNextPage]);
  const handleLoadMore = async () => {
    await Promise.all(loadMoreTargets.map(item_0 => item_0.fetch()));
  };
  const [selectedFaq, setSelectedFaq] = useState<TFaq | null>(null);
  const handleOpenFaq = (item_1: any) => {
    if (item_1 && item_1.raw && item_1.category === "faq") {
      setSelectedFaq(item_1.raw);
    }
  };
  return <>
      <section className="pt-20 pb-[60px] sm:pt-[100px] sm:pb-[80px] relative" style={{
      background: "linear-gradient(264.23deg, #134E67 4.59%, #1DB0DE 80.88%)"
    }}>
        <Image src="/images/search-hero-pattern.webp" alt="Search hero" width={1440} height={344} className="object-cover object-center absolute top-[50%] left-[50%] transform translate-x-[-50%] translate-y-[-50%] container !px-0 h-full" />
        <div className="mx-auto lg:w-[872px] px-4 relative">
          <h1 className="text-white text-5xl sm:text-[56px] font-bold sm:leading-[3.5rem]">{pageData?.title || "Pencarian"}</h1>
          <form onSubmit={handleSubmitSearch} className="mt-7 sm:mt-10 flex items-center sm:gap-4">
            <SearchInput onSearch={handleSearch} value={keyword} appendChild={<div className="flex items-center justify-center px-4 z-20 bg-white h-[58px] rounded-l-xl">
                  <MagnifyingGlassIcon className="size-6 text-gray-900" />
                </div>} buttonClassName="hidden" inputClassName="rounded-xl py-7 relative font-medium text-lg bg-white text-gray-900 rounded-l-none rounded-tr-none sm:rounded-tr-xl rounded-br-none sm:rounded-br-xl z-10 -ml-1" placeholder={pageData?.inputPlaceholder || "Cari..."} />
            <div className="bg-white sm:bg-transparent rounded-r-xl h-[58px] flex items-center justify-center px-1 z-20 relative -ml-1">
              <Button size="lg" className="font-bold text-lg sm:text-xl h-[50px] sm:h-[58px] rounded-xl" type="submit" disabled={isFetching}>
                {pageData?.buttonText || "Cari"}
              </Button>
            </div>
          </form>
        </div>
      </section>
      <section className="pt-[60px] pb-[60px] sm:pb-20 bg-[#FDFDFD]">
        <div className="mx-auto lg:w-[872px] px-4 relative">
          <div>
            <Combobox fallbackText="Semua Kategori" placeholder="Semua Kategori" clearable initialValue={null} options={Object.keys(searchCategories).map(category => {
            return {
              value: category,
              label: searchCategories[category as SearchCategory]
            };
          })} onChange={handleChangeCategory} onClear={handleClearCategory} appendIcon={<ChevronDownIcon className="size-5 min-w-5" />} />
          </div>
          <div className="mt-8 space-y-6">
            {isFetching && <div className="flex items-center justify-center text-center">
                <span className="w-8 h-8 rounded-full border-t-2 border-l-2 border-r-2  border-primary inline-block animate-spin"></span>
              </div>}
            {!isFetching && (!combinedResults || combinedResults && combinedResults.length <= 0 ? <div className="items-center justify-center text-center flex flex-col">
                  <h2 className="font-bold text-3xl">Hasil pencarian tidak ditemukan</h2>
                  <p className="text-gray-700 text-lg">
                    Kami tidak bisa menemukan hasil untuk &quot;{keyword}&quot;.
                    <br />
                    Coba gunakan kata kunci lain dan ulangi pencarian.
                  </p>
                </div> : null)}
            {combinedResults.map((item_2, index) => {
            return <div key={item_2.category + "-" + item_2.title + "-" + index} className="bg-white px-4 py-5 sm:px-7 sm:py-6 border border-gray-200 flex gap-3 items-start overflow-hidden relative rounded-2xl">
                  <SearchCategoryIcon category={item_2.category} size="lg" />
                  <div className="flex-1 relative w-[calc(100%_-_52px)] overflow-hidden">
                    <div className="text-base font-bold text-gray-500 uppercase tracking-widest">
                      {item_2.url ? <Link href={item_2.url}>{searchCategories[item_2.category as SearchCategory]}</Link> : <a className="cursor-pointer" onClick={() => handleOpenFaq(item_2)}>
                          {searchCategories[item_2.category as SearchCategory]}
                        </a>}
                    </div>
                    <h4 className="font-semibold text-xl sm:text-2xl mt-3 sm:mt-4">
                      {item_2.url ? <Link href={item_2.url}>{item_2.title}</Link> : <a className="cursor-pointer" onClick={() => handleOpenFaq(item_2)}>
                          {item_2.title}
                        </a>}
                    </h4>
                    {item_2.description && <div className="mt-2 truncate overflow-hidden relative hidden-text text-lg text-gray-700">
                        <HtmlParser data={item_2.description} />
                      </div>}
                    <div className="mt-6">
                      {item_2.url ? <Button variant="link" className="p-0 m-0 font-semibold text-primary-600" asChild>
                          <Link href={item_2.url}>
                            {pageData?.detailButtonText || "Lihat Selengkapnya"}
                            <ArrowRightIcon className="size-5 ml-1" />
                          </Link>
                        </Button> : <Button variant="link" className="p-0 m-0 font-semibold text-primary-600" onClick={() => handleOpenFaq(item_2)}>
                          {pageData?.detailButtonText || "Lihat Selengkapnya"}
                          <ArrowRightIcon className="size-5 ml-1" />
                        </Button>}
                    </div>
                  </div>
                </div>;
          })}
          </div>
          {loadMoreTargets.length > 0 ? <div className="mt-[60px] text-center flex items-center justify-center">
              <Button variant="outline" className="text-primary text-lg h-14 md:px-8 border-primary hover:text-primary rounded-xl py-4 px-6 font-bold" onClick={handleLoadMore}>
                {pageData?.moreButtonText || "Muat Lebih Banyak"}
              </Button>
            </div> : <div className="mt-[60px] text-lg font-medium text-gray-400 text-center">
              {pageData?.allResultDisplayedText || "Semua hasil pencarian sudah ditampilkan"}
            </div>}
        </div>
      </section>
      <FaqSearchDialog faq={selectedFaq} onOpenChange={open => setSelectedFaq(!open ? null : selectedFaq)} />
    </>;
}